import { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Typography,
  Popper,
  ClickAwayListener,
  InputAdornment,
  makeStyles,
} from '@material-ui/core';
import InputField from '../inputField';
import VirtualizedList from '../virtualizedList';
import ActionButton from '../actionButton';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';
import VirtualPreset from './virtualPreset';
import SavePresetDialog from './savePresetDialog';
import { ReactComponent as StorageIcon } from '../../../assets/icons/storage.svg';
import { ellipsis } from '../../../constants/helperCssRules';
import {
  forbiddenCharacters,
  validateLength,
} from '../../../utility/validation';
import {
  isArrayEmpty,
  trimString,
  isValueInList,
} from '../../../utility/helpers';
import { useCustomEffect } from '../../../utility/hooks';
import { customSearch } from '../../../utility/uiUtils';
import { setPopperWidth } from '../peoplePicker/config';

const PRESET_ITEM_HEIGHT = 40;
const PRESET_LIST_GAP = 4;

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  picker: {
    display: 'flex',
    position: 'relative',
  },
  pickerInput: {
    width: '100%',
  },
  inputRoot: {
    flexWrap: 'nowrap',
  },
  savePresetIcon: {
    marginRight: 0,
  },
  placeholder: {
    '& .MuiFormControl-root .MuiInputBase-root': {
      '& > input': {
        ...ellipsis(),
      },
      '& > input::placeholder': {
        color: primary.black,
      },
    },
  },
  popperRoot: {
    boxShadow: 'none',
    borderRadius: 4,
    zIndex: 1,
  },
  popperMain: {
    boxSizing: 'border-box',
    backgroundColor: primary.white,
    border: `1px solid ${primary.bluish6}`,
    height: 320,
    borderRadius: 4,
    padding: spacing(4),
    marginTop: spacing(1),
  },
  popoverFooter: {
    display: 'flex',
    marginTop: spacing(4),
    paddingTop: 24,
    borderTop: `1px solid ${primary.bluish7}`,
    justifyContent: 'space-between',
    '& .MuiFormControl-root': {
      width: 235,
    },
  },
  presetScrollWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 288,
  },
  presetList: {
    height: '100%',
    width: '100%',
  },
  presetsViewport: {
    height: 320,
  },
  item: {
    borderRadius: 4,
  },
  presetItem: {
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    padding: spacing(2),
    justifyContent: 'space-between',
  },
  presetName: {
    maxWidth: 'calc(100% - 6px)',
  },
  button: {
    display: 'flex',
    flexShrink: 0,
    width: 24,
    height: 24,
  },
  buttonIcon: {
    display: 'block',
    width: 18,
    height: 18,
  },
  noResults: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollY: {
    right: -11,
    '& .os-scrollbar-vertical': {
      padding: spacing(2.75, 0),
    },
  },
  hover: {
    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: primary.bluish8,
    },
  },
  selectButton: {
    marginLeft: 'auto',
  },
  selectedPreset: {
    backgroundColor: primary.bluish9,
    '& > div > p': {
      fontFamily: 'ProximaNova-Bold',
    },
    '& > p': {
      fontFamily: 'ProximaNova-Bold',
    },
  },
}));

const PresetDropdown = ({
  className = undefined,
  paperClass = null,
  translations,
  activePreset = -1,
  options = [],
  dropdownWidth = null,
  inputWidth = 0,
  disabledPreset = '',
  alreadySavedMessage = '',
  presetPlaceholderName,
  presetSummary = {},
  isDisabled,
  handleSavePreset,
  handlePlaceholder,
  handleDelete,
  handleApply,
}) => {
  const classes = useStyles();
  const inputRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState('');
  const [presets, setPresets] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');

  const savePresetTooltip = disabledPreset || alreadySavedMessage;
  const isSavePresetDisabled =
    Boolean(disabledPreset) || Boolean(alreadySavedMessage);
  const shouldFlip = inputWidth ? anchorEl?.clientWidth < inputWidth : false;

  const getEmptyPresetsMessage = () => {
    if (trimString(search)) {
      return translations.noSearchResults;
    }
    if (!isSavePresetDisabled) {
      return translations.saveCurrentSelection;
    }
    return translations.createNewPreset;
  };

  const getVirtualPresetSize = useCallback(() => PRESET_ITEM_HEIGHT, []);

  const handleClose = () => {
    setIsOpen(false);
    setSearch('');
    setPresets([]);
    setHasError(false);
    setErrorMessage('');
    setIsFocused(false);
  };

  const onSetAnchorEl = () => setAnchorEl(inputRef?.current);

  const handleOpen = () => {
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const getPreset = (term = '') => {
    const searchTerm = trimString(term);
    const newPresets = customSearch(options, searchTerm);

    return setPresets(newPresets);
  };

  const handleChange = event => {
    const { value } = event.target;

    setSearch(value);
    handleOpen();
    return getPreset(value);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setIsFocused(true);
  };

  const handleValidation = name => {
    let message = '';
    message = validateLength(name, 0, 100)
      ? message
      : translations.limitErrorMessage;
    message = forbiddenCharacters(name)
      ? message
      : translations.forbiddenCharacters;
    message = trimString(name) ? message : translations.emptyErrorMessage;
    const alreadyUsed = isValueInList(
      options.map(preset => preset.name),
      trimString(name)
    );

    if (message || alreadyUsed) {
      if (alreadyUsed) {
        setHasError(true);
        setErrorMessage(translations.availableName);
      } else {
        setHasError(true);
        setErrorMessage(message);
      }
      return false;
    }
    if (!message && !alreadyUsed && hasError) {
      setHasError(false);
      return true;
    }
    return true;
  };

  const handlePresetName = e => {
    const name = e.target.value;

    handleValidation(name);
    setNewPresetName(name);
  };

  const handleOpenSaveModal = () => {
    setIsSaveModalOpen(true);
  };

  const handleCloseSaveModal = () => {
    setIsSaveModalOpen(false);
    setNewPresetName('');
    setHasError(false);
    setErrorMessage('');
  };

  const handleModalSave = () => {
    const isValid = handleValidation(newPresetName);

    if (isValid) {
      handleCloseSaveModal();
      handlePlaceholder(newPresetName);
      handleSavePreset(trimString(newPresetName));
    }
  };

  const applyPreset = useCallback(
    preset => {
      handlePlaceholder(preset.name);
      handleApply(preset);
    },
    [handleApply, handlePlaceholder]
  );

  useCustomEffect(onSetAnchorEl);

  useCustomEffect(
    () => {
      if (isFocused) {
        getPreset();
      }
    },
    [isFocused],
    false
  );

  const renderPreset = useCallback(
    preset => {
      const isPresetSelected = preset?.id === activePreset;

      return (
        <VirtualPreset
          translations={translations}
          preset={preset}
          isSelected={isPresetSelected}
          onApply={applyPreset}
          onDelete={handleDelete}
          onPlaceholder={handlePlaceholder}
        />
      );
    },
    [translations, activePreset, applyPreset, handleDelete, handlePlaceholder]
  );

  const renderSavePresetAdornment = () => (
    <InputAdornment position="end">
      <ActionButton
        className={classes.savePresetIcon}
        iconClass={classes.buttonIcon}
        type={ACTION_BUTTON_TYPES.REPORT_CREATE}
        isDisabled={isSavePresetDisabled}
        disabledTooltipText={savePresetTooltip}
        onClickHandler={e => {
          e.stopPropagation();
          handleOpenSaveModal();
        }}
      />
    </InputAdornment>
  );

  return (
    <div className={className}>
      <ClickAwayListener onClickAway={handleClose} mouseEvent="onMouseDown">
        <div className={classes.picker}>
          <div
            className={classNames(classes.pickerInput, {
              [classes.placeholder]:
                presetPlaceholderName !== translations.startSearch,
            })}
            ref={inputRef}
          >
            <InputField
              label={translations.label}
              placeholder={presetPlaceholderName}
              value={search}
              disabled={isDisabled}
              adornmentIcon={StorageIcon}
              isIconOnStart
              customRootClass={classes.inputRoot}
              endAdornment={renderSavePresetAdornment()}
              onChange={handleChange}
              onInputFocus={handleInputFocus}
              autoComplete="off"
              fullWidth
            />
          </div>
          <Popper
            className={classNames(classes.popperRoot, paperClass)}
            style={{
              width: anchorEl ? anchorEl.clientWidth : null,
            }}
            anchorEl={anchorEl}
            open={isOpen}
            modifiers={{
              flip: {
                enabled: true,
                options: {
                  altBoundary: true,
                  rootBoundary: 'viewport',
                  allowedAutoPlacements: ['bottom-end'],
                  fallbackPlacements: ['bottom-end'],
                },
                preventOverflow: {
                  enabled: true,
                  altBoundary: true,
                  escapeWithReference: true,
                  boundariesElement: 'viewport',
                },
              },
            }}
            popperOptions={{
              onCreate: ({ instance: { reference, popper } }) =>
                setPopperWidth(reference, popper, dropdownWidth),
              onUpdate: ({ instance: { reference, popper } }) =>
                setPopperWidth(reference, popper, dropdownWidth),
            }}
            placement="bottom-start"
            disablePortal
            keepMounted={!!shouldFlip}
          >
            <div className={classes.popperMain}>
              <div className={classes.presetScrollWrapper}>
                {isOpen && !isArrayEmpty(presets) && (
                  <VirtualizedList
                    className={classes.presetList}
                    viewportClass={classes.presetsViewport}
                    verticalScrollClass={classes.scrollY}
                    items={presets}
                    count={presets?.length ?? 0}
                    gap={PRESET_LIST_GAP}
                    estimateSize={getVirtualPresetSize}
                    renderItem={renderPreset}
                  />
                )}
                {isOpen && isArrayEmpty(presets) && (
                  <Typography className={classes.noResults}>
                    {getEmptyPresetsMessage()}
                  </Typography>
                )}
              </div>
            </div>
          </Popper>
        </div>
      </ClickAwayListener>
      <SavePresetDialog
        translations={translations}
        isOpened={isSaveModalOpen}
        presetName={newPresetName}
        hasError={hasError}
        errorMessage={errorMessage}
        isConfirmDisabled={isSavePresetDisabled}
        presetSummary={presetSummary}
        onClose={handleCloseSaveModal}
        onConfirm={handleModalSave}
        onPresetNameChange={handlePresetName}
      />
    </div>
  );
};

PresetDropdown.propTypes = {
  className: PropTypes.string,
  paperClass: PropTypes.string,
  activePreset: PropTypes.number,
  dropdownWidth: PropTypes.string,
  inputWidth: PropTypes.number,
  handleApply: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleSavePreset: PropTypes.func.isRequired,
  alreadySavedMessage: PropTypes.string,
  presetSummary: PropTypes.shape({
    translations: PropTypes.shape({
      surveyType: PropTypes.string,
      range: PropTypes.string,
      attribute: PropTypes.string,
      attributes: PropTypes.string,
      scores: PropTypes.string,
      people: PropTypes.string,
      subject: PropTypes.string,
      reviewers: PropTypes.string,
    }).isRequired,
    hasSurveyType: PropTypes.bool,
    hasRange: PropTypes.bool,
    hasAttributes: PropTypes.bool,
    hasSingleAttribute: PropTypes.bool,
    hasScores: PropTypes.bool,
    hasUserList: PropTypes.bool,
    hasSubjectList: PropTypes.bool,
    hasReviewerList: PropTypes.bool,
    hasCompareFilters: PropTypes.bool,
    hasFilterSections: PropTypes.bool,
    filterSections: PropTypes.arrayOf(PropTypes.object),
    surveyTypeLabel: PropTypes.string,
    range: PropTypes.shape({
      startDate: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      endDate: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
    }),
    attributes: PropTypes.arrayOf(PropTypes.object),
    attribute: PropTypes.object,
    scores: PropTypes.arrayOf(PropTypes.number),
    users: PropTypes.arrayOf(PropTypes.object),
    subjectUsers: PropTypes.arrayOf(PropTypes.object),
    reviewerUsers: PropTypes.arrayOf(PropTypes.object),
    compareFilters: PropTypes.arrayOf(PropTypes.object),
    checkIsSelectable: PropTypes.func,
  }),
  translations: PropTypes.object.isRequired,
  disabledPreset: PropTypes.string,
  handlePlaceholder: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})),
};

export default PresetDropdown;
